import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import type { CommentAnchor } from "@/hooks/use-highlight-marks";
import { openInFocus } from "@/lib/focus-frame-activation";
import { frameFloatsHere } from "@/lib/frame-screen-rect";
import { anchorPositionFor } from "@/components/comments/comment-popover-anchor";
import { useCanvasStore } from "@/stores/canvas-store";
import { useTasksStore } from "@/stores/tasks-store";

/**
 * The comment anchors of one frame (its `text_anchor` tasks) and the badge
 * click that opens their comments, shared by markdown frames and Docs.
 */
export function useFrameCommentAnchors(frameId: string, frameTitle: string) {
  // Scope the subscription to THIS frame's anchored tasks. `useShallow`
  // compares the resulting array element-by-element (the task references are
  // stable in the store while unchanged), so this frame only re-renders when
  // its own tasks change — not on every unrelated task add/update/remove.
  const frameTasks = useTasksStore(
    useShallow((s) =>
      s.tasks.filter((t) => t.frame === frameId && t.text_anchor?.text),
    ),
  );
  const commentAnchors: CommentAnchor[] = useMemo(
    () =>
      frameTasks.map((t) => ({
        text: t.text_anchor!.text,
        taskId: t.id,
        commentCount: (t.comment_count ?? 0) + 1,
      })),
    [frameTasks],
  );
  const openCommentPopover = useCanvasStore((s) => s.openCommentPopover);
  const handleAnchorBadgeClick = useCallback(
    (taskId: string, badgeEl: HTMLElement) => {
      // The scoped popover opens at the clicked badge. A floating window
      // stays as it is; otherwise the frame opens in focus first, and an
      // already-open window is selected in place.
      const position = anchorPositionFor(badgeEl);
      if (!frameFloatsHere(frameId)) {
        openInFocus([frameId], { origin: "comment" });
      }
      openCommentPopover({
        frameId,
        frameTitle,
        position,
        scopedTaskId: taskId,
      });
    },
    [frameId, frameTitle, openCommentPopover],
  );
  return { commentAnchors, handleAnchorBadgeClick };
}
